import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import {
    ChevronLeft,
    ChevronRight,
    XLg
} from "react-bootstrap-icons";
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    isToday,
    isBefore,
    startOfDay,
    setHours,
    setMinutes,
    parseISO,
    isValid
} from 'date-fns';
import "./index.css";

interface OrderEditDateTimePickerProps {
    label: string;
    dateValue: string | null;
    timeValue: string | null;
    onConfirm: (dateTime: string) => void;
    minDate?: Date | null;
    maxDate?: Date | null;
    className?: string;
    placeholder?: string;
}

const OrderEditDateTimePicker = ({
    label,
    dateValue,
    timeValue,
    onConfirm,
    minDate,
    maxDate,
    className,
    placeholder
}: OrderEditDateTimePickerProps) => {
    const [showModal, setShowModal] = useState(false);
    const [viewDate, setViewDate] = useState(dateValue ? new Date(dateValue) : new Date());
    const [tempDate, setTempDate] = useState<Date | null>(dateValue ? new Date(dateValue) : null);
    const [tempTime, setTempTime] = useState<string | null>(timeValue || "09:00");

    useEffect(() => {
        if (dateValue) {
            const d = new Date(dateValue);
            if (isValid(d)) {
                setViewDate(d);
                setTempDate(d);
            }
        }
        if (timeValue) {
            setTempTime(timeValue);
        }
    }, [dateValue, timeValue]);

    const handleOpen = () => {
        if (dateValue) {
            const d = new Date(dateValue);
            if (isValid(d)) {
                setViewDate(d);
                setTempDate(d);
            }
        }
        setTempTime(timeValue || "09:00");
        setShowModal(true);
    };

    const handleClose = () => setShowModal(false);

    const handleConfirm = () => {
        if (tempDate && tempTime) {
            const year = tempDate.getFullYear();
            const month = String(tempDate.getMonth() + 1).padStart(2, '0');
            const day = String(tempDate.getDate()).padStart(2, '0');
            const dateTimeString = `${year}-${month}-${day}T${tempTime}`;
            onConfirm(dateTimeString);
            handleClose();
        }
    };

    const handleMonthPrev = () => setViewDate(subMonths(viewDate, 1));
    const handleMonthNext = () => setViewDate(addMonths(viewDate, 1));

    const days = useMemo(() => {
        const start = startOfWeek(startOfMonth(viewDate));
        const end = endOfWeek(endOfMonth(viewDate));
        return eachDayOfInterval({ start, end });
    }, [viewDate]);

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const timeSlots = useMemo(() => {
        const slots = [];
        for (let h = 9; h <= 22; h++) {
            for (let m = 0; m < 60; m += 15) {
                if (h === 22 && m > 0) break;
                const displayTime = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                slots.push({ displayTime, value: displayTime });
            }
        }
        return slots;
    }, []);

    const displayValue = useMemo(() => {
        if (!dateValue) return "";
        try {
            const d = new Date(dateValue);
            if (!isValid(d)) return "";
            const datePart = format(d, 'dd-MM-yyyy');
            return timeValue ? `${datePart} ${timeValue}` : datePart;
        } catch (e) {
            return "";
        }
    }, [dateValue, timeValue]);

    return (
        <div className={`order-edit-dt-container ${className || ""}`}>
            <div className="order-edit-dt-wrapper" onClick={handleOpen}>
                <label className="form-label dashboard-poppins-font-style">{label}</label>
                <div className="input-date-container">
                    <input
                        readOnly
                        value={displayValue}
                        placeholder={placeholder || "Select date & time"}
                        className="merchant-input-style-create-product w-100"
                        type="text"
                    />
                </div>
            </div>

            <Modal
                show={showModal}
                onHide={handleClose}
                centered
                dialogClassName="custom-datetime-modal"
                contentClassName="custom-datetime-content"
                size="lg"
            >
                <div className="custom-dt-header">
                    <h5 className="custom-dt-title">Select {label}</h5>
                    <button className="custom-dt-close" onClick={handleClose}>
                        <XLg size={18} />
                    </button>
                </div>

                <div className="custom-dt-body">
                    <div className="custom-dt-calendar-section">
                        <div className="custom-dt-month-nav">
                            <span className="custom-dt-current-month">{format(viewDate, 'MMMM yyyy')}</span>
                            <div className="custom-dt-nav-btns">
                                <button onClick={handleMonthPrev}><ChevronLeft size={16} /></button>
                                <button onClick={handleMonthNext}><ChevronRight size={16} /></button>
                            </div>
                        </div>

                        <div className="custom-dt-calendar-grid">
                            {weekDays.map(day => <div key={day} className="custom-dt-weekday">{day}</div>)}
                            {days.map((day, idx) => {
                                const isCurrentMonth = isSameMonth(day, viewDate);
                                const isSelected = tempDate && isSameDay(day, tempDate);
                                const isAfterMax = maxDate ? day > maxDate : false;
                                const isBeforeMin = minDate ? isBefore(day, startOfDay(minDate)) : false;
                                const isDisable = isAfterMax || isBeforeMin;

                                return (
                                    <button
                                        key={idx}
                                        className={`custom-dt-day 
                                            ${!isCurrentMonth ? 'is-outside' : ''} 
                                            ${isSelected ? 'is-selected' : ''} 
                                            ${isToday(day) ? 'is-today' : ''}
                                            ${isDisable ? 'is-disabled' : ''}
                                        `}
                                        onClick={() => !isDisable && setTempDate(day)}
                                        disabled={isDisable}
                                        type="button"
                                    >
                                        {format(day, 'd')}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="custom-dt-time-section">
                        <div className="dt-info-box">
                            <label>Selected Date & Time</label>
                            <div className="dt-info-value">
                                {tempDate ? format(tempDate, 'dd MMM yyyy') : 'No Date Selected'}
                                {tempTime && <span className="dt-time-display"> {tempTime}</span>}
                            </div>
                        </div>

                        <div className="dt-time-picker-box">
                            <label>Select Time</label>
                            <div className="dt-time-grid">
                                {timeSlots.map(slot => (
                                    <button
                                        key={slot.value}
                                        className={`dt-time-btn ${tempTime === slot.value ? 'is-selected' : ''}`}
                                        onClick={() => setTempTime(slot.value)}
                                        type="button"
                                    >
                                        {slot.displayTime}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="custom-dt-footer">
                    <Button variant="link" className="custom-dt-cancel" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button className="custom-dt-confirm" onClick={handleConfirm} disabled={!tempDate}>
                        Confirm Date & Time
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default OrderEditDateTimePicker;
